import { useEffect, useMemo } from 'react';
import { useState } from 'owa-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import type { OwaHeaderButtonControl, OwaHeaderButtonControls } from 'owa-header-types';

interface AsyncVisibilityResult {
    promise: Promise<boolean>;
    isVisible: boolean;
}

interface AsyncVisibilityState {
    [key: string]: AsyncVisibilityResult;
}

export default function useHeaderButtonVisibility(
    buttons: OwaHeaderButtonControls
): OwaHeaderButtonControl[] {
    const [asyncVisibilityMap, setAsyncVisibilityMap] =
        /* eslint-disable-next-line owa-custom-rules/prefer-react-state-without-arrays-or-objects -- (https://aka.ms/OWALintWiki)
         * Using a local React state object is preferred over Satchel store for this component-specific visibility tracking.
         * Rationale:
         * 1. Component-scoped state: Button visibility is only relevant to this component and doesn't need global sharing
         * 2. Encapsulation: Keeps the complexity of async visibility resolution contained within this hook
         * 3. Simplification: Eliminates the need for Satchel mutators, orchestrators, and selectors for this local concern
         * 4. Multiple primitives would be impractical: Having separate useState for each dynamic button would create
         *    an unpredictable number of state variables and make the code harder to maintain
         * This object represents a cohesive unit of related boolean states that belong together. */
        useState<AsyncVisibilityState>({});

    // Each button's `isVisible()` may read MobX observables (e.g. the module-context mailbox for the
    // consumer Rewards button). `useComputedValue` runs this getter inside a MobX `computed` whose
    // `.get()` executes during the render of the observer `OwaHeaderButtonGroup`, so any observable
    // touched by any `isVisible()` is tracked automatically and re-derives visibility on change —
    // no need to hand-thread each observable as an explicit dependency.
    const visibilityResults = useComputedValue(() => {
        const results: Record<string, boolean | Promise<boolean>> = {};

        for (const button of buttons) {
            if (!button.isVisible) {
                // No visibility function means always visible
                results[button.id] = true;
            } else {
                try {
                    results[button.id] = button.isVisible();
                } catch {
                    results[button.id] = false;
                }
            }
        }

        return results;
    }, [buttons]);

    useEffect(() => {
        let cancelled = false;

        for (const button of buttons) {
            const result = visibilityResults[button.id];

            if (result instanceof Promise) {
                result
                    .then(isVisible => {
                        if (!cancelled) {
                            setAsyncVisibilityMap(prevState => ({
                                ...prevState,
                                [button.id]: {
                                    promise: result,
                                    isVisible,
                                },
                            }));
                        }
                    })
                    .catch(() => {
                        if (!cancelled) {
                            setAsyncVisibilityMap(prevState => ({
                                ...prevState,
                                [button.id]: {
                                    promise: result,
                                    isVisible: false,
                                },
                            }));
                        }
                    });
            }
        }

        return () => {
            cancelled = true;
        };
    }, [buttons, visibilityResults]);

    return useMemo(() => {
        return buttons.filter(button => {
            const result = visibilityResults[button.id];

            if (result instanceof Promise) {
                const asyncVisibility = asyncVisibilityMap[button.id];
                return asyncVisibility?.promise === result ? asyncVisibility.isVisible : false;
            } else {
                return result;
            }
        });
    }, [buttons, visibilityResults, asyncVisibilityMap]);
}
